import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Tabs,
  Tab,
  Typography,
  Avatar,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  InputAdornment,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  DialogActions,
  DialogContentText,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { navigateAfterImpersonation } from '../../services/auth/postImpersonationRedirect';
import type { CompanyUser } from '../../services/SuperAdminService';
import LoginIcon from '@mui/icons-material/Login';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GroupsIcon from '@mui/icons-material/Groups';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CategoryIcon from '@mui/icons-material/Category';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PaymentIcon from '@mui/icons-material/Payment';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import TableBarIcon from '@mui/icons-material/TableBar';
import SuperAdminService from '../../services/SuperAdminService';

interface CompanyDetailsModalProps {
  open: boolean;
  onClose: () => void;
  company: {
    id: string;
    name: string;
    logo?: string;
    admin: {
      name: string;
      email: string;
      phone?: string;
    };
    maxPersonnel?: number;
    createdAt?: string;
  } | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-tabpanel-${index}`}
      aria-labelledby={`company-tab-${index}`}
      {...other}
      style={{ height: '100%' }}
    >
      {value === index && (
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const CompanyDetailsModal: React.FC<CompanyDetailsModalProps> = ({
  open,
  onClose,
  company,
}) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [taskData, setTaskData] = useState<any>(null);
  const [hrData, setHrData] = useState<any>(null);
  const [accountingData, setAccountingData] = useState<any>(null);
  const [salesSystemData, setSalesSystemData] = useState<any>(null);
  const [branchesData, setBranchesData] = useState<any>(null);
  // Kullanıcılar sekmesi (impersonation seçim listesi)
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[] | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [impersonatingId, setImpersonatingId] = useState<number | null>(null);
  const [confirmUser, setConfirmUser] = useState<CompanyUser | null>(null);
  const [impError, setImpError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { impersonate } = useAuth();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    // Sekme değiştiğinde ilgili veriyi yükle
    loadTabData(newValue);
  };

  // Sekme verilerini yükle
  const loadTabData = async (tabIndex: number) => {
    if (!company) return;
    
    // Tab 0: Görev Takip
    if (tabIndex === 0) {
      setLoading(true);
      try {
        const superAdminService = SuperAdminService.getInstance();
        const data = await superAdminService.getCompanyTasks(company.id);
        setTaskData(data);
      } catch (error) {
        console.error('Görev verileri yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    }
    // Tab 1: İnsan Kaynakları
    else if (tabIndex === 1) {
      setLoading(true);
      try {
        const superAdminService = SuperAdminService.getInstance();
        const data = await superAdminService.getCompanyHR(company.id);
        setHrData(data);
      } catch (error) {
        console.error('İK verileri yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    }
    // Tab 2: Ön Muhasebe
    else if (tabIndex === 2) {
      setLoading(true);
      try {
        const superAdminService = SuperAdminService.getInstance();
        const data = await superAdminService.getCompanyAccounting(company.id);
        setAccountingData(data);
      } catch (error) {
        console.error('Muhasebe verileri yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    }
    // Tab 3: Satış Sistemi
    else if (tabIndex === 3) {
      setLoading(true);
      try {
        const superAdminService = SuperAdminService.getInstance();
        const data = await superAdminService.getCompanySalesSystem(company.id);
        
        // QR Menü verilerini .NET API'den al (Node.js API'den değil!)
        const qrMenuData = await superAdminService.getCompanyQRMenus(company.id);
        
        // QR Menü verilerini sales system data'ya ekle
        data.qrMenu = qrMenuData;
        
        // Çağrı Merkezi verilerini .NET API'den al (CallCenterPage.tsx gibi)
        const callCenterData = await superAdminService.getCompanyCallCenter(company.id);
        
        // Çağrı Merkezi verilerini sales system data'ya ekle
        data.callCenter = callCenterData;
        
        setSalesSystemData(data);
      } catch (error) {
        console.error('Satış sistemi verileri yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    }
    // Tab 4: Şubeler
    else if (tabIndex === 4) {
      setLoading(true);
      try {
        const superAdminService = SuperAdminService.getInstance();
        const data = await superAdminService.getCompanyBranches(company.id);
        console.log('🏢 [CompanyDetailsModal] Şube verileri alındı:', data);
        setBranchesData(data);
      } catch (error) {
        console.error('❌ [CompanyDetailsModal] Şube verileri yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    }
    // Tab 5: Kullanıcılar (impersonation seçim listesi)
    else if (tabIndex === 5) {
      setLoading(true);
      try {
        const superAdminService = SuperAdminService.getInstance();
        const users = await superAdminService.getCompanyUsers(company.id);
        setCompanyUsers(users);
      } catch (error) {
        console.error('Kullanıcılar yüklenirken hata:', error);
        setCompanyUsers([]);
      } finally {
        setLoading(false);
      }
    }
    // Diğer sekmeler için veri yükleme buraya eklenecek
  };

  // Onay modalını açar (ikon tıklaması).
  const requestImpersonate = (u: CompanyUser) => {
    setConfirmUser(u);
  };

  // Onaylanan kullanıcıya geç: token al → modalları kapat → role göre yönlendir.
  const confirmImpersonate = async () => {
    if (!company || !confirmUser) return;
    const u = confirmUser;
    setImpersonatingId(u.id);
    try {
      const authUser = await impersonate({ targetUserId: u.id });
      setConfirmUser(null);
      onClose();
      await navigateAfterImpersonation(
        { role: u.role, companyId: company.id, uid: authUser.uid },
        (path) => navigate(path)
      );
    } catch (e: any) {
      console.error('[CompanyDetailsModal] Impersonation hatası:', e);
      setConfirmUser(null);
      setImpError(e?.message || 'Kullanıcı oturumuna geçilemedi.');
    } finally {
      setImpersonatingId(null);
    }
  };

  // Modal açıldığında veya şirket değiştiğinde veriyi temizle ve yeniden yükle
  useEffect(() => {
    if (open && company) {
      // Şirket değiştiğinde tüm verileri temizle
      setTaskData(null);
      setHrData(null);
      setAccountingData(null);
      setSalesSystemData(null);
      setBranchesData(null);
      setCompanyUsers(null);
      setUserSearch('');
      setCurrentTab(0);
      loadTabData(0);
    } else if (!open) {
      // Modal kapandığında verileri temizle
      setTaskData(null);
      setHrData(null);
      setAccountingData(null);
      setSalesSystemData(null);
      setBranchesData(null);
      setCompanyUsers(null);
      setUserSearch('');
      setCurrentTab(0);
    }
  }, [open, company?.id]);

  if (!company) return null;

  // Tarih formatı
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR');
    } catch (error) {
      return '-';
    }
  };

  // Tab tanımlamaları
  const tabs = [
    { label: 'Görev Takip', icon: <TrackChangesIcon />, id: 'task_tracking' },
    { label: 'İnsan Kaynakları', icon: <SupervisedUserCircleIcon />, id: 'hr' },
    { label: 'Ön Muhasebe', icon: <AccountBalanceWalletIcon />, id: 'accounting' },
    { label: 'Satış Sistemi', icon: <StorefrontIcon />, id: 'sales_system' },
    { label: 'Şubeler', icon: <BusinessIcon />, id: 'branches' },
    { label: 'Kullanıcılar', icon: <PeopleAltIcon />, id: 'users' },
  ];

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '85vh',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Modal Başlığı */}
      <DialogTitle
        sx={{
          bgcolor: '#1976d2',
          color: 'white',
          py: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 2.5, flex: 1 }}>
            {/* Logo ve Şirket Adı */}
            <Avatar
              src={company.logo}
              alt={company.name}
              sx={{ width: 64, height: 64, bgcolor: 'white', color: '#1976d2', fontSize: '1.5rem' }}
            >
              {company.name.charAt(0)}
            </Avatar>
            
            {/* Şirket Bilgileri */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                {company.name}
              </Typography>
              
              {/* Bilgi Satırları - 3 sütun, dar gap */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: 0.75,
                rowGap: 0.5
              }}>
                {/* Admin Bilgisi */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ opacity: 0.95, fontSize: '0.85rem' }}>
                    <strong>Admin:</strong> {company.admin.name}
                  </Typography>
                </Box>

                {/* Email */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ opacity: 0.95, fontSize: '0.85rem' }}>
                    {company.admin.email}
                  </Typography>
                </Box>

                {/* Telefon */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ opacity: 0.95, fontSize: '0.85rem' }}>
                    {company.admin.phone || 'Telefon Bilgisi Yok'}
                  </Typography>
                </Box>

                {/* Max Personel */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleAltIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ opacity: 0.95, fontSize: '0.85rem' }}>
                    <strong>Max Personel:</strong> {company.maxPersonnel || 0}
                  </Typography>
                </Box>

                {/* Kayıt Tarihi */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ opacity: 0.95, fontSize: '0.85rem' }}>
                    <strong>Kayıt:</strong> {formatDate(company.createdAt)}
                  </Typography>
                </Box>

                {/* Şirket ID */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FingerprintIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      opacity: 0.95, 
                      fontSize: '0.8rem',
                      fontFamily: 'monospace',
                      bgcolor: 'rgba(255,255,255,0.15)',
                      px: 1,
                      py: 0.25,
                      borderRadius: 0.5
                    }}
                  >
                    {company.id}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Kapatma Butonu */}
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              ml: 2,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      {/* Tab Başlıkları */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: '#f5f5f5',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
            },
            '& .Mui-selected': {
              color: '#1976d2',
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#1976d2',
              height: 3,
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.id}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              id={`company-tab-${index}`}
              aria-controls={`company-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab İçerikleri */}
      <DialogContent
        sx={{
          flex: 1,
          overflow: 'hidden',
          p: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Görev Takip */}
            <TabPanel value={currentTab} index={0}>
              {!taskData ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Görev verileri yükleniyor...
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {/* İstatistik Kartları */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <AssignmentIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                            {taskData.summary.total}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Toplam Görev
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: '#e8f5e9', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                            {taskData.summary.completed}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Tamamlanan
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: '#ffebee', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <CancelIcon sx={{ fontSize: 40, color: '#f44336', mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                            {taskData.summary.missed}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Kaçırılan
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: '#fff3e0', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <PendingActionsIcon sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                            {taskData.summary.pending}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Bekleyen
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Görev Tipi Dağılımı */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Görev Tipi Dağılımı
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          icon={<AssignmentIcon />}
                          label={`Normal: ${taskData.byType.normal}`}
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          icon={<GroupsIcon />}
                          label={`Takım: ${taskData.byType.team}`}
                          color="secondary"
                          variant="outlined"
                        />
                        <Chip
                          icon={<TodayIcon />}
                          label={`Günlük: ${taskData.byType.daily}`}
                          color="success"
                          variant="outlined"
                        />
                        <Chip
                          icon={<DateRangeIcon />}
                          label={`Haftalık: ${taskData.byType.weekly}`}
                          color="info"
                          variant="outlined"
                        />
                        <Chip
                          icon={<CalendarMonthIcon />}
                          label={`Aylık: ${taskData.byType.monthly}`}
                          color="warning"
                          variant="outlined"
                        />
                        <Chip
                          icon={<EventAvailableIcon />}
                          label={`Yıllık: ${taskData.byType.yearly}`}
                          color="error"
                          variant="outlined"
                        />
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Son Eklenen Görevler */}
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Son Eklenen Görevler
                      </Typography>
                      {taskData.recentActivities && taskData.recentActivities.length > 0 ? (
                        <List sx={{ maxHeight: 280, overflow: 'auto' }}>
                          {taskData.recentActivities.slice(0, 5).map((activity: any, index: number) => {
                            // Görev tipi rengini belirle
                            const getTaskTypeColor = (type: string) => {
                              switch(type) {
                                case 'Normal': return '#1976d2';
                                case 'Günlük': return '#4caf50';
                                case 'Takım': return '#9c27b0';
                                case 'Haftalık': return '#ff9800';
                                case 'Aylık': return '#f44336';
                                case 'Yıllık': return '#e91e63';
                                default: return '#757575';
                              }
                            };
                            
                            // Görev tipi ikonunu belirle
                            const getTaskTypeIcon = (type: string) => {
                              switch(type) {
                                case 'Normal': return <AssignmentIcon sx={{ color: getTaskTypeColor(type) }} />;
                                case 'Günlük': return <TodayIcon sx={{ color: getTaskTypeColor(type) }} />;
                                case 'Takım': return <GroupsIcon sx={{ color: getTaskTypeColor(type) }} />;
                                case 'Haftalık': return <DateRangeIcon sx={{ color: getTaskTypeColor(type) }} />;
                                case 'Aylık': return <CalendarMonthIcon sx={{ color: getTaskTypeColor(type) }} />;
                                case 'Yıllık': return <EventAvailableIcon sx={{ color: getTaskTypeColor(type) }} />;
                                default: return <AssignmentIcon sx={{ color: getTaskTypeColor(type) }} />;
                              }
                            };
                            
                            return (
                              <React.Fragment key={index}>
                                <ListItem>
                                  <ListItemIcon>
                                    {getTaskTypeIcon(activity.taskType)}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" component="span">
                                          {activity.taskName}
                                        </Typography>
                                        <Chip 
                                          label={activity.taskType} 
                                          size="small" 
                                          sx={{ 
                                            height: 20,
                                            fontSize: '0.7rem',
                                            bgcolor: `${getTaskTypeColor(activity.taskType)}20`,
                                            color: getTaskTypeColor(activity.taskType)
                                          }}
                                        />
                                      </Box>
                                    }
                                    secondary={
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">
                                          {new Date(activity.createdAt).toLocaleString('tr-TR', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}
                                        </Typography>
                                        {activity.projectName && (
                                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                            • Proje: {activity.projectName}
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                  />
                                </ListItem>
                                {index < 4 && <Divider />}
                              </React.Fragment>
                            );
                          })}
                        </List>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                          Henüz görev bulunmuyor
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              )}
            </TabPanel>

            {/* İnsan Kaynakları */}
            <TabPanel value={currentTab} index={1}>
              {!hrData ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    İK verileri yükleniyor...
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {/* Özet Kartları */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <PeopleAltIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                            {hrData.summary.totalPersonnel}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Toplam Personel
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: '#f3e5f5', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <EventAvailableIcon sx={{ fontSize: 40, color: '#9c27b0', mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                            {hrData.summary.totalShifts}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Toplam Vardiya
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: '#fff3e0', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <BusinessIcon sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                            {hrData.summary.totalBranches}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Toplam Şube
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: '#e8f5e9', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <SupervisedUserCircleIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                            {hrData.summary.totalDepartments}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Toplam Departman
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {/* Rol Dağılımı */}
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Rol Dağılımı
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Chip
                                label="Admin"
                                color="error"
                                size="small"
                                sx={{ minWidth: 100 }}
                              />
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {hrData.personnel.byRole.admin}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Chip
                                label="Yönetici"
                                color="warning"
                                size="small"
                                sx={{ minWidth: 100 }}
                              />
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {hrData.personnel.byRole.manager}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Chip
                                label="Personel"
                                color="default"
                                size="small"
                                sx={{ minWidth: 100 }}
                              />
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {hrData.personnel.byRole.employee}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Vardiya Bilgileri */}
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Vardiya Bilgileri
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Vardiyaya Atanan
                              </Typography>
                              <Chip
                                label={hrData.shifts.personnelInShifts}
                                color="success"
                                size="small"
                              />
                            </Box>
                            <Divider />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Vardiyası Olmayan
                              </Typography>
                              <Chip
                                label={hrData.shifts.unassignedToShift}
                                color="default"
                                size="small"
                              />
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Departman ve Şube Dağılımı */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {/* Departman Dağılımı */}
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Departman Dağılımı
                          </Typography>
                          {Object.keys(hrData.personnel.byDepartment).length > 0 ? (
                            <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                              {Object.entries(hrData.personnel.byDepartment).map(([dept, count]: [string, any], index: number) => (
                                <React.Fragment key={index}>
                                  <ListItem>
                                    <ListItemText
                                      primary={dept}
                                      secondary={`${count} personel`}
                                    />
                                    <Chip label={count} size="small" color="primary" />
                                  </ListItem>
                                  {index < Object.keys(hrData.personnel.byDepartment).length - 1 && <Divider />}
                                </React.Fragment>
                              ))}
                              {hrData.personnel.unassignedToDepartment > 0 && (
                                <>
                                  <Divider />
                                  <ListItem>
                                    <ListItemText
                                      primary="Departmanı Olmayan"
                                      secondary={`${hrData.personnel.unassignedToDepartment} personel`}
                                    />
                                    <Chip label={hrData.personnel.unassignedToDepartment} size="small" color="default" />
                                  </ListItem>
                                </>
                              )}
                            </List>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                              Henüz departman bulunmuyor
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Şube Dağılımı */}
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Şube Dağılımı
                          </Typography>
                          {Object.keys(hrData.personnel.byBranch).length > 0 ? (
                            <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                              {Object.entries(hrData.personnel.byBranch).map(([branch, count]: [string, any], index: number) => (
                                <React.Fragment key={index}>
                                  <ListItem>
                                    <ListItemText
                                      primary={branch}
                                      secondary={`${count} personel`}
                                    />
                                    <Chip label={count} size="small" color="primary" />
                                  </ListItem>
                                  {index < Object.keys(hrData.personnel.byBranch).length - 1 && <Divider />}
                                </React.Fragment>
                              ))}
                              {hrData.personnel.unassignedToBranch > 0 && (
                                <>
                                  <Divider />
                                  <ListItem>
                                    <ListItemText
                                      primary="Şubesi Olmayan"
                                      secondary={`${hrData.personnel.unassignedToBranch} personel`}
                                    />
                                    <Chip label={hrData.personnel.unassignedToBranch} size="small" color="default" />
                                  </ListItem>
                                </>
                              )}
                            </List>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                              Henüz şube bulunmuyor
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Son Eklenen Personeller */}
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Son Eklenen Personeller
                      </Typography>
                      {hrData.recentPersonnel && hrData.recentPersonnel.length > 0 ? (
                        <List sx={{ maxHeight: 280, overflow: 'auto' }}>
                          {hrData.recentPersonnel.map((person: any, index: number) => {
                            const getRoleColor = (role: string) => {
                              switch(role) {
                                case 'admin': return '#f44336';
                                case 'manager': return '#ff9800';
                                case 'employee': return '#757575';
                                default: return '#757575';
                              }
                            };
                            
                            const getRoleLabel = (role: string) => {
                              switch(role) {
                                case 'admin': return 'Admin';
                                case 'manager': return 'Yönetici';
                                case 'employee': return 'Personel';
                                default: return 'Personel';
                              }
                            };
                            
                            return (
                              <React.Fragment key={index}>
                                <ListItem>
                                  <ListItemIcon>
                                    <PersonIcon sx={{ color: getRoleColor(person.role) }} />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" component="span">
                                          {person.name}
                                        </Typography>
                                        <Chip 
                                          label={getRoleLabel(person.role)} 
                                          size="small" 
                                          sx={{ 
                                            height: 20,
                                            fontSize: '0.7rem',
                                            bgcolor: `${getRoleColor(person.role)}20`,
                                            color: getRoleColor(person.role)
                                          }}
                                        />
                                      </Box>
                                    }
                                    secondary={
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">
                                          {new Date(person.addedAt).toLocaleString('tr-TR', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}
                                        </Typography>
                                        {person.departmentName && (
                                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                            • Departman: {person.departmentName}
                                          </Typography>
                                        )}
                                        {person.branchName && (
                                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                            • Şube: {person.branchName}
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                  />
                                </ListItem>
                                {index < 4 && <Divider />}
                              </React.Fragment>
                            );
                          })}
                        </List>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                          Henüz personel bulunmuyor
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              )}
            </TabPanel>

            {/* Ön Muhasebe */}
            <TabPanel value={currentTab} index={2}>
              {!accountingData ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Muhasebe verileri yükleniyor...
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {/* Üst Bölüm - 4 Özet Kartı */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: '#e8f5e9', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <LocalAtmIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                            ₺{accountingData.summary.totalCashBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Toplam Kasa
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <AccountBalanceIcon sx={{ fontSize: 40, color: '#2196f3', mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196f3' }}>
                            ₺{accountingData.summary.totalBankBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Toplam Banka
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: '#fff3e0', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <TrendingUpIcon sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                            ₺{accountingData.summary.totalReceivables.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Toplam Alacak
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: '#ffebee', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <TrendingDownIcon sx={{ fontSize: 40, color: '#f44336', mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                            ₺{accountingData.summary.totalPayables.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Toplam Borç
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Orta Bölüm - 2 Sütun */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {/* Sol Sütun - Envanter Özeti */}
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InventoryIcon /> Envanter Özeti
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#f3e5f5', borderRadius: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                                  {accountingData.inventory.totalProducts}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Toplam Ürün
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                                  {accountingData.inventory.activeProducts}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Aktif Ürün
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#fff3e0', borderRadius: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                                  {accountingData.inventory.warehouseCount}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Depo Sayısı
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2196f3' }}>
                                  ₺{accountingData.inventory.totalStockValue.toLocaleString('tr-TR')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Stok Değeri
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CategoryIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                  <Typography variant="body2">Kategori</Typography>
                                </Box>
                                <Chip label={accountingData.inventory.categoryCount} size="small" color="primary" />
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <BrandingWatermarkIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                  <Typography variant="body2">Marka</Typography>
                                </Box>
                                <Chip label={accountingData.inventory.brandCount} size="small" color="primary" />
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Sağ Sütun - Fatura/Teklif Özeti */}
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ReceiptIcon /> Fatura & Teklif Özeti
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                                  {accountingData.invoices.salesCount}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Satış Faturası
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#ffebee', borderRadius: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                                  {accountingData.invoices.purchaseCount}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Alış Faturası
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                Son 30 Gün
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Satış</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                                  ₺{accountingData.invoices.totalSalesAmount.toLocaleString('tr-TR')}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Alış</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                                  ₺{accountingData.invoices.totalPurchaseAmount.toLocaleString('tr-TR')}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, bgcolor: '#f3e5f5', borderRadius: 1, px: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <ShoppingCartIcon sx={{ fontSize: 20, color: '#9c27b0' }} />
                                  <Typography variant="body2">Hızlı Satış</Typography>
                                </Box>
                                <Chip label={accountingData.invoices.quickSalesCount} size="small" sx={{ bgcolor: '#9c27b0', color: 'white' }} />
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <LocalOfferIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                  <Typography variant="body2">Teklif</Typography>
                                </Box>
                                <Chip label={accountingData.offers.pendingCount} size="small" color="warning" />
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                                <Typography variant="body2" color="text.secondary">Müşteri/Tedarikçi</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {accountingData.accounts.customerCount}/{accountingData.accounts.supplierCount}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Alt Bölüm - Son Aktiviteler */}
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Son İşlemler
                      </Typography>
                      {accountingData.recentActivities && accountingData.recentActivities.length > 0 ? (
                        <List sx={{ maxHeight: 280, overflow: 'auto' }}>
                          {accountingData.recentActivities.map((activity: any, index: number) => {
                            const getActivityIcon = (type: string) => {
                              switch(type) {
                                case 'invoice': return <ReceiptIcon sx={{ color: '#2196f3' }} />;
                                case 'receipt': return <TrendingUpIcon sx={{ color: '#4caf50' }} />;
                                case 'payment': return <TrendingDownIcon sx={{ color: '#f44336' }} />;
                                default: return <PaymentIcon sx={{ color: '#757575' }} />;
                              }
                            };
                            
                            const getActivityColor = (type: string) => {
                              switch(type) {
                                case 'invoice': return '#2196f3';
                                case 'receipt': return '#4caf50';
                                case 'payment': return '#f44336';
                                default: return '#757575';
                              }
                            };
                            
                            return (
                              <React.Fragment key={index}>
                                <ListItem>
                                  <ListItemIcon>
                                    {getActivityIcon(activity.type)}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" component="span">
                                          {activity.description}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: getActivityColor(activity.type) }}>
                                          {activity.currency} {activity.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </Typography>
                                      </Box>
                                    }
                                    secondary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          {new Date(activity.date).toLocaleString('tr-TR', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}
                                        </Typography>
                                        {activity.accountName && (
                                          <Typography variant="caption" color="text.secondary">
                                            • {activity.accountName}
                                          </Typography>
                                        )}
                                        {activity.paymentMethod && (
                                          <Chip 
                                            label={activity.paymentMethod} 
                                            size="small" 
                                            sx={{ height: 18, fontSize: '0.65rem' }}
                                          />
                                        )}
                                      </Box>
                                    }
                                  />
                                </ListItem>
                                {index < 4 && <Divider />}
                              </React.Fragment>
                            );
                          })}
                        </List>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                          Henüz işlem bulunmuyor
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              )}
            </TabPanel>

            {/* Satış Sistemi */}
            <TabPanel value={currentTab} index={3}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : !salesSystemData ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <StorefrontIcon sx={{ fontSize: 64, color: '#1976d2', mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Veri yüklenemedi
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {/* Üst Bölüm - Modül Kartları */}
                  <Grid container spacing={2}>
                    {/* Hızlı Satış */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ bgcolor: salesSystemData.quickSale.isActive ? '#e3f2fd' : '#f5f5f5' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PointOfSaleIcon sx={{ color: salesSystemData.quickSale.isActive ? '#1976d2' : '#9e9e9e', fontSize: 32 }} />
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: salesSystemData.quickSale.isActive ? '#1976d2' : '#757575' }}>
                                Hızlı Satış
                              </Typography>
                            </Box>
                            <Chip 
                              label={salesSystemData.quickSale.isActive ? 'Aktif' : 'Pasif'} 
                              color={salesSystemData.quickSale.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </Box>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                  {salesSystemData.quickSale.screenCount}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Satış Ekranı
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                                  {salesSystemData.quickSale.pykCount}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  PYK Ekranı
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Restoran */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ bgcolor: salesSystemData.restaurant.isActive ? '#fff3e0' : '#f5f5f5' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <RestaurantMenuIcon sx={{ color: salesSystemData.restaurant.isActive ? '#f57c00' : '#9e9e9e', fontSize: 32 }} />
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: salesSystemData.restaurant.isActive ? '#f57c00' : '#757575' }}>
                                Restoran
                              </Typography>
                            </Box>
                            <Chip 
                              label={salesSystemData.restaurant.isActive ? 'Aktif' : 'Pasif'} 
                              color={salesSystemData.restaurant.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </Box>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                                  {salesSystemData.restaurant.setupCount}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Kurulum
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                                  {salesSystemData.restaurant.tableCount}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Masa
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* QR Menü */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ bgcolor: salesSystemData.qrMenu.isActive ? '#e8f5e9' : '#f5f5f5' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <QrCodeIcon sx={{ color: salesSystemData.qrMenu.isActive ? '#388e3c' : '#9e9e9e', fontSize: 32 }} />
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: salesSystemData.qrMenu.isActive ? '#388e3c' : '#757575' }}>
                                QR Menü
                              </Typography>
                            </Box>
                            <Chip 
                              label={salesSystemData.qrMenu.isActive ? 'Aktif' : 'Pasif'} 
                              color={salesSystemData.qrMenu.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </Box>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#388e3c' }}>
                                  {salesSystemData.qrMenu.menuCount}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Menü
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#388e3c' }}>
                                  {salesSystemData.qrMenu.tableCount}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Masa
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* E-Ticaret */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ bgcolor: salesSystemData.ecommerce.hasSettings ? '#f3e5f5' : '#f5f5f5' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ShoppingCartIcon sx={{ color: salesSystemData.ecommerce.hasSettings ? '#7b1fa2' : '#9e9e9e', fontSize: 32 }} />
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: salesSystemData.ecommerce.hasSettings ? '#7b1fa2' : '#757575' }}>
                                E-Ticaret
                              </Typography>
                            </Box>
                            <Chip 
                              label={salesSystemData.ecommerce.hasSettings ? 'Ayarlanmış' : 'Ayarlanmamış'} 
                              color={salesSystemData.ecommerce.hasSettings ? 'success' : 'default'}
                              size="small"
                            />
                          </Box>
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                              {salesSystemData.ecommerce.memberCount}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Kayıtlı Üye
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Kurye */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ bgcolor: salesSystemData.courier.isActive ? '#e1f5fe' : '#f5f5f5' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <DeliveryDiningIcon sx={{ color: salesSystemData.courier.isActive ? '#0288d1' : '#9e9e9e', fontSize: 32 }} />
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: salesSystemData.courier.isActive ? '#0288d1' : '#757575' }}>
                                Kurye Sistemi
                              </Typography>
                            </Box>
                            <Chip 
                              label={salesSystemData.courier.isActive ? 'Aktif' : 'Pasif'} 
                              color={salesSystemData.courier.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </Box>
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0288d1' }}>
                              {salesSystemData.courier.courierCount}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Kayıtlı Kurye
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Çağrı Merkezi */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ bgcolor: salesSystemData.callCenter.isActive ? '#fce4ec' : '#f5f5f5' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <SupportAgentIcon sx={{ color: salesSystemData.callCenter.isActive ? '#c2185b' : '#9e9e9e', fontSize: 32 }} />
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: salesSystemData.callCenter.isActive ? '#c2185b' : '#757575' }}>
                                Çağrı Merkezi
                              </Typography>
                            </Box>
                            <Chip 
                              label={salesSystemData.callCenter.isActive ? 'Aktif' : 'Pasif'} 
                              color={salesSystemData.callCenter.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </Box>
                          <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#c2185b' }}>
                              {salesSystemData.callCenter.screenCount}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Çağrı Ekranı
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Özet Bilgi */}
                  <Card sx={{ mt: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        📊 Satış Modülleri Özeti
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Chip 
                              label={`${[
                                salesSystemData.quickSale.isActive,
                                salesSystemData.restaurant.isActive,
                                salesSystemData.qrMenu.isActive,
                                salesSystemData.ecommerce.hasSettings, // E-Ticaret için hasSettings kullan
                                salesSystemData.courier.isActive,
                                salesSystemData.callCenter.isActive
                              ].filter(Boolean).length} / 6`}
                              color="primary"
                              sx={{ fontSize: '1rem', fontWeight: 'bold', height: 32 }}
                            />
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                              Aktif Modül
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Chip 
                              label={salesSystemData.quickSale.screenCount + salesSystemData.quickSale.pykCount + salesSystemData.callCenter.screenCount}
                              color="info"
                              sx={{ fontSize: '1rem', fontWeight: 'bold', height: 32 }}
                            />
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                              Toplam Ekran
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Chip 
                              label={salesSystemData.restaurant.tableCount + salesSystemData.qrMenu.tableCount}
                              color="success"
                              sx={{ fontSize: '1rem', fontWeight: 'bold', height: 32 }}
                            />
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                              Toplam Masa
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </TabPanel>

            {/* Şubeler */}
            <TabPanel value={currentTab} index={4}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                  <CircularProgress />
                </Box>
              ) : branchesData ? (
                <Box>
                  {/* Özet Kart */}
                  <Card sx={{ mb: 3, bgcolor: '#e3f2fd' }}>
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <BusinessIcon sx={{ fontSize: 64, color: '#1976d2', mb: 2 }} />
                      <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        {branchesData.branchCount || 0}
                      </Typography>
                      <Typography variant="h6" color="text.secondary">
                        Toplam Şube
                      </Typography>
                    </CardContent>
                  </Card>

                  {branchesData.branchCount === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        Bu şirkete henüz şube eklenmemiş.
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Veri yüklenemedi
                  </Typography>
                </Box>
              )}
            </TabPanel>

            {/* Kullanıcılar — impersonation seçim listesi */}
            <TabPanel value={currentTab} index={5}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                  <CircularProgress />
                </Box>
              ) : companyUsers ? (
                <Box>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="İsim, e-posta veya role göre ara..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {(() => {
                    const q = userSearch.trim().toLowerCase();
                    const filtered = companyUsers.filter((u) => {
                      if (!q) return true;
                      const hay = `${u.firstName} ${u.lastName} ${u.email} ${u.role}`.toLowerCase();
                      return hay.includes(q);
                    });

                    if (companyUsers.length === 0) {
                      return (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            Bu şirkette aktif kullanıcı bulunamadı.
                          </Typography>
                        </Box>
                      );
                    }

                    return (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Ad Soyad</TableCell>
                              <TableCell>E-posta</TableCell>
                              <TableCell>Rol</TableCell>
                              <TableCell align="right" />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filtered.map((u) => {
                              const fullName = `${u.firstName} ${u.lastName}`.trim() || '—';
                              const busy = impersonatingId === u.id;
                              return (
                                <TableRow key={u.id} hover>
                                  <TableCell>{fullName}</TableCell>
                                  <TableCell>{u.email}</TableCell>
                                  <TableCell>
                                    <Chip label={u.role || '-'} size="small" variant="outlined" />
                                  </TableCell>
                                  <TableCell align="right">
                                    {/* Nötr, dikkat çekmeyen geçiş ikonu */}
                                    <Tooltip title="Bu kullanıcının oturumuna geç">
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={impersonatingId !== null}
                                          onClick={() => requestImpersonate(u)}
                                          sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
                                        >
                                          {busy ? <CircularProgress size={16} /> : <LoginIcon fontSize="small" />}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            {filtered.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Aramayla eşleşen kullanıcı yok.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    );
                  })()}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Veri yüklenemedi
                  </Typography>
                </Box>
              )}
            </TabPanel>
          </>
        )}
      </DialogContent>
    </Dialog>

    {/* Impersonation onay modalı (alert/confirm yerine) */}
    <Dialog
      open={!!confirmUser}
      onClose={() => impersonatingId === null && setConfirmUser(null)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LoginIcon color="primary" fontSize="small" />
        Kullanıcı oturumuna geç
      </DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          {confirmUser && (
            <>
              <Box sx={{ mb: 1 }}>
                Aşağıdaki kullanıcının oturumuna geçmek üzeresiniz:
              </Box>
              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {`${confirmUser.firstName} ${confirmUser.lastName}`.trim() || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {confirmUser.email}
                </Typography>
                <Chip label={confirmUser.role || '-'} size="small" variant="outlined" sx={{ mt: 0.5 }} />
              </Box>
              <Box sx={{ mt: 1.5, fontSize: 13, color: 'text.secondary' }}>
                Bu kullanıcının gördüğü ekranlar açılacaktır. İstediğiniz an sağ alttaki
                butonla yönetici hesabınıza dönebilirsiniz.
              </Box>
            </>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => setConfirmUser(null)}
          disabled={impersonatingId !== null}
          color="inherit"
        >
          İptal
        </Button>
        <Button
          onClick={confirmImpersonate}
          variant="contained"
          disabled={impersonatingId !== null}
          startIcon={impersonatingId !== null ? <CircularProgress size={16} color="inherit" /> : <LoginIcon />}
        >
          Geç
        </Button>
      </DialogActions>
    </Dialog>

    {/* Hata bildirimi (alert yerine) */}
    <Snackbar
      open={!!impError}
      autoHideDuration={6000}
      onClose={() => setImpError(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="error" variant="filled" onClose={() => setImpError(null)} sx={{ width: '100%' }}>
        {impError}
      </Alert>
    </Snackbar>
    </>
  );
};

export default CompanyDetailsModal;

